/**
 * 对局状态机 —— 核心游戏状态定义
 *
 * 立直麻将一局的完整流程：
 *   配牌 → [摸牌 → 打牌 → (鸣牌判定)] × N → 和了 / 流局
 *
 * 状态设计原则：
 *   · 所有状态**不可变**（reducer 风格：给定状态 + 动作 → 新状态）
 *   · 随机性通过注入的 random 函数控制（便于测试可复现）
 *   · 合法性校验与状态变更分离（canDo / do）
 */

import type { Tile } from './tile.js';
import type { Mentsu } from './agari.js';
import type { Wind, WinContext } from './yaku/types.js';

// ════════════════════════════════════════════════════════════════
// 基本类型
// ════════════════════════════════════════════════════════════════

/** 座位（0=自家/起家，1=下家，2=对家，3=上家） */
export type Seat = 0 | 1 | 2 | 3;

/** 局阶段 */
export type Phase =
  | 'init' // 配牌前
  | 'draw' // 等待摸牌
  | 'discard' // 摸牌后，等待打牌（或自摸/暗杠/加杠/立直）
  | 'claim' // 有人打牌后，等待其他家鸣牌/荣和
  | 'over'; // 本局结束（和了或流局）

/** 玩家状态 */
export interface PlayerState {
  /** 座位 */
  readonly seat: Seat;
  /** 手牌（不含副露，牌数由 phase 决定） */
  readonly hand: readonly Tile[];
  /** 副露的面子 */
  readonly melds: readonly Mentsu[];
  /** 舍牌河 */
  readonly discards: readonly Tile[];
  /** 是否立直 */
  readonly riichi: boolean;
  /** 立直宣言的那一巡（用于一发判定） */
  readonly riichiTurn: number | null;
  /**
   * 立直宣言牌在自己舍牌数组中的下标（用于 UI 横置显示）
   *
   * ⚠️ 不能用「discards.length - 1」代替 —— 那是【最后一张】，
   *    立直后每打一张都会"移动"，导致横置的牌跟着跑（踩过）。
   */
  readonly riichiDiscardIndex: number | null;
  /** 是否振听（简化：有振听标记） */
  readonly furiten: boolean;
  /** 点数 */
  readonly score: number;
  /** 是否已和牌（本局） */
  readonly won: boolean;
  /** 是否已听牌（流局时用于罚符） */
  readonly tenpaiAtDraw: boolean;
}

/** 局信息 */
export interface RoundInfo {
  /** 场风 */
  readonly roundWind: Wind;
  /** 第几局（1-4 = 东1-东4） */
  readonly roundNumber: 1 | 2 | 3 | 4;
  /** 本场数（连庄次数） */
  readonly honba: number;
  /** 供托点数（立直棒） */
  readonly riichiSticks: number;
}

/** 牌山 */
export interface Wall {
  /** 可摸的牌（活牌，从前往后取） */
  readonly live: readonly Tile[];
  /** 岭上牌（杠后摸） */
  readonly dead: readonly Tile[];
  /** 宝牌指示牌（初始 1 张，杠后翻开更多） */
  readonly doraIndicators: readonly Tile[];
  /** 里宝牌指示牌 */
  readonly uraIndicators: readonly Tile[];
  /** 已翻开的宝牌指示牌数量 */
  readonly doraRevealed: number;
}

/** 游戏状态 */
export interface GameState {
  /** 当前阶段 */
  readonly phase: Phase;
  /** 局信息 */
  readonly round: RoundInfo;
  /** 4 家状态（索引 = 座位） */
  readonly players: readonly PlayerState[];
  /** 当前行动者 */
  readonly activeSeat: Seat;
  /** 当前巡数（用于立直/一发） */
  readonly turn: number;
  /** 牌山 */
  readonly wall: Wall;
  /** 最后打出的牌（等待鸣牌判定时） */
  readonly lastDiscard: { tile: Tile; bySeat: Seat } | null;
  /** 本局结果 */
  readonly result: RoundResult | null;
  /**
   * 刚摸到的牌的 id（UI 用于高亮）
   *
   * ⚠️ 不能用「手牌最后一张」推断 —— doDraw 会对手牌排序，
   *    摸到的牌会被插到中间（踩过：UI 手牌显示乱序）。
   */
  readonly drawnTileId?: number | null;
  /**
   * 规则变体配置（天凤 / 雀魂 可切换）
   *
   * ⚠️ 可选字段 —— 既有测试夹具手动构造 GameState 时不必补它；
   *    读取一律走 `rulesOf(state)`（缺省 = 天凤预设）。
   */
  readonly rules?: import('./rule-config.js').RuleConfig;
  /**
   * 包牌（責任払い）责任归属
   *
   * 某人「鸣牌」凑齐大三元（第 3 个三元牌刻子）或大四喜（第 4 个风牌刻子）时，
   * 记下其责任人（打出被鸣之牌的那家）。和牌结算时按预设分摊。
   */
  readonly pao?: readonly PaoClaim[];
}

/** 包牌责任 */
export interface PaoClaim {
  /** 受益者（鸣牌凑齐役满者） */
  readonly owner: Seat;
  /** 责任者（打出被鸣之牌的人） */
  readonly liable: Seat;
  readonly kind: 'daisangen' | 'daisuushii';
}

/** 本局结果 */
export type RoundResult =
  | {
      readonly kind: 'tsumo';
      readonly winner: Seat;
      readonly winTile: Tile;
      readonly scoreChanges: readonly number[];
    }
  | {
      readonly kind: 'ron';
      readonly winner: Seat;
      readonly loser: Seat;
      readonly winTile: Tile;
      readonly scoreChanges: readonly number[];
    }
  | {
      readonly kind: 'draw';
      readonly reason:
        | 'exhaustive'
        | 'nine_terminals'
        | 'four_winds'
        | 'four_riichi'
        | 'four_kans'
        | 'triple_ron';
      /** 听牌者（用于罚符） */
      readonly tenpaiSeats: readonly Seat[];
      readonly scoreChanges: readonly number[];
    };

// ════════════════════════════════════════════════════════════════
// 动作
// ════════════════════════════════════════════════════════════════

/** 动作类型 */
export type Action =
  | { readonly type: 'draw' } // 摸牌
  | { readonly type: 'discard'; readonly tile: Tile; readonly riichi?: boolean } // 打牌（可同时宣言立直）
  | { readonly type: 'tsumo' } // 自摸和牌
  | { readonly type: 'ron' } // 荣和
  | { readonly type: 'chi'; readonly tiles: readonly Tile[] } // 吃
  | { readonly type: 'pon' } // 碰（tiles 由引擎从手牌自动选取）
  | { readonly type: 'kan'; readonly kind: 'ankan' | 'minkan' | 'kakan'; readonly tile: Tile } // 杠
  | { readonly type: 'pass' } // 跳过鸣牌
  | { readonly type: 'riichi_declare' } // 立直宣言（在打牌前）
  | { readonly type: 'abortive_draw'; readonly reason: 'nine_terminals' | 'triple_ron' }; // 途中流局宣言（九种九牌/三家和）

/** 动作合法性 */
export interface ActionValidation {
  readonly legal: boolean;
  readonly reason?: string;
}

// ════════════════════════════════════════════════════════════════
// 常量
// ════════════════════════════════════════════════════════════════

/** 每家初始点数 */
export const STARTING_SCORE = 25000;

/** 立直棒点数 */
export const RIICHI_STICK_VALUE = 1000;

/** 初始手牌数（庄家 14 张 = 13 + 摸的第一张） */
export const INITIAL_HAND_SIZE = 13;

/** 岭上牌数量（4 张） */
export const DEAD_WALL_SIZE = 4;

/** 座位 → 自风（由【庄家】决定：庄家=东，其下家=南，依次西/北） */
export function seatToWind(seat: Seat, round: RoundInfo): Wind {
  const winds: Wind[] = ['east', 'south', 'west', 'north'];
  // ⚠️ 自风必须随【庄家】轮换 —— 旧实现按场风偏移，等于把自风钉死在场风上
  //    （东场时 0 号座位恒为东，看起来「庄家/自风从不轮换」）。
  return winds[(seat - dealerSeat(round) + 4) % 4];
}

/** 风 → 座位 */
export function windToSeat(wind: Wind): Seat {
  const winds: Wind[] = ['east', 'south', 'west', 'north'];
  return winds.indexOf(wind) as Seat;
}

/** 下家座位 */
export function nextSeat(seat: Seat): Seat {
  return ((seat + 1) % 4) as Seat;
}

/** 上家座位 */
export function prevSeat(seat: Seat): Seat {
  return ((seat + 3) % 4) as Seat;
}

/** 对家座位 */
export function acrossSeat(seat: Seat): Seat {
  return ((seat + 2) % 4) as Seat;
}

/**
 * 庄家座位
 *
 * 庄家由【局数】决定（与场风无关）：东1局坐 0 号、东2局坐 1 号…东4局 3 号，
 * 南1局回到 0 号。连庄不改座位，只由 honba 表示。
 *
 * ⚠️ 旧实现 `windToSeat(roundWind)` —— 东场恒为 0 号、南场恒为 1 号，
 *    导致整局下来庄家从不轮换（用户报「似乎始终是庄家」）。
 */
export function dealerSeat(round: RoundInfo): Seat {
  return ((round.roundNumber - 1) % 4) as Seat;
}

/** 家是否听牌（流局罚符用） */
export function isTenpai(player: PlayerState): boolean {
  return player.tenpaiAtDraw;
}
